import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '..';
import {
  ReleaseFieldType,
  ReleaseSongFieldType,
  AdvancedReleaseSongFieldType,
} from './types';

export const selectSelf = (state: RootState) => state.distribution;

export const selectGenres = createSelector(
  selectSelf,
  (distribution) => distribution.genres
);

export const selectReleases = createSelector(
  selectSelf,
  (distribution) => distribution.releases
);

export const selectReleaseRequests = createSelector(
  selectSelf,
  (distribution) => distribution.releaseRequests
);

export const selectReleaseRequest = (songId: string) =>
  createSelector(
    selectSelf,
    (distribution) => distribution.releaseRequests[songId]
  );

export const selectRelease = (releaseId: string) =>
  createSelector(
    selectSelf,
    (distribution) => distribution.releases[releaseId]
  );

export const selectReleaseSongs = (releaseId: string) =>
  createSelector(
    [selectRelease(releaseId)],
    (currentRelease) => currentRelease.songs
  );

export const selectReleaseTitle = (releaseId: string) =>
  createSelector(
    [selectRelease(releaseId)],
    (currentRelease) => currentRelease[ReleaseFieldType.SongTitle]
  );

export const selectReleaseArtistName = (releaseId: string) =>
  createSelector(
    [selectRelease(releaseId)],
    (currentRelease) => currentRelease[ReleaseFieldType.ArtistName]
  );

export const selectReleaseGenre = (releaseId: string) =>
  createSelector(
    [selectRelease(releaseId)],
    (currentRelease) => currentRelease[ReleaseFieldType.Genre]
  );

export const selectReleaseCoverArt = (releaseId: string) =>
  createSelector(
    [selectRelease(releaseId)],
    (currentRelease) => currentRelease[ReleaseFieldType.CoverArt]
  );

export const selectReleaseSongComposerName = (releaseId: string) =>
  createSelector(
    [selectReleaseSongs(releaseId)],
    (releaseSong) => releaseSong[0]?.metadata[ReleaseSongFieldType.ComposerName]
  );

export const selectReleaseSongVocalistName = (releaseId: string) =>
  createSelector(
    [selectReleaseSongs(releaseId)],
    (releaseSong) => releaseSong[0]?.metadata[ReleaseSongFieldType.VocalistName]
  );

export const selectReleaseSongHasVocals = (releaseId: string) =>
  createSelector(
    [selectReleaseSongs(releaseId)],
    (releaseSong) => releaseSong[0].metadata[ReleaseSongFieldType.HasVocals]
  );

export const selectReleaseVocalsContent = (releaseId: string) =>
  createSelector(
    [selectReleaseSongs(releaseId)],
    (releaseSong) => releaseSong[0].metadata[ReleaseSongFieldType.VocalsContent]
  );
export const selectReleaseSongLanguage = (releaseId: string) =>
  createSelector(
    [selectReleaseSongs(releaseId)],
    (releaseSong) => releaseSong[0].metadata[ReleaseSongFieldType.Language]
  );

export const selectReleaseSongAdvancedOptions = (
  releaseId: string,
  field: AdvancedReleaseSongFieldType
) =>
  createSelector(
    [selectReleaseSongs(releaseId)],
    (releaseSong) => releaseSong[0].metadata[field]
  );
